/**
 * K-T-7 — Layer-3 hook for the canonical passport (profile-level)
 * technician summary tuple.
 *
 * Thin wrapper around `kpi_passport_summary(profile_id, start_at,
 * end_at)`. Holds NO local KPI math — the RPC is the source of truth.
 * See `supabase/migrations/20260430160000_kpi_passport_summary.sql`
 * for the canonical formulas and the fan-out across
 * `technician_employment_history`.
 *
 * Sibling to `useTechnicianSummary` (K-T-6, shop-scoped). Returns the
 * same `TechnicianSummaryResult` shape via the
 * `PassportSummaryResult` alias so the two consumer surfaces share a
 * type.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PassportSummaryResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UsePassportSummaryOptions {
  /** Technician profile UUID (NOT a shop_technician_id). */
  profileId: string;
  /** ISO timestamp. Inclusive lower bound of the half-open window. */
  startAt: string;
  /** ISO timestamp. Exclusive upper bound. */
  endAt: string;
}

export interface UsePassportSummaryResult {
  data: PassportSummaryResult | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Returns the canonical profile-level (cross-shop) technician summary
 * tuple. Lifetime semantics are achieved by passing a wide window
 * (e.g. `1900-01-01` → `2099-12-31`); narrower windows are supported
 * for future date-bounded passport views.
 *
 * Stable query key:
 *   ['kpi', 'kpi_passport_summary', profileId, startAt, endAt]
 */
export function usePassportSummary(
  opts: UsePassportSummaryOptions,
): UsePassportSummaryResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_passport_summary',
      opts.profileId,
      opts.startAt,
      opts.endAt,
    ],
    queryFn: async (): Promise<PassportSummaryResult | null> => {
      // Cast through unknown until supabase types.ts codegen catches up
      // to the new RPC. The return shape is pinned by the migration's
      // RETURNS TABLE clause and the `PassportSummaryResult` alias.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_profile_id: string;
          p_start_at: string;
          p_end_at: string;
        },
      ) => Promise<{
        data: PassportSummaryResult[] | null;
        error: unknown;
      }>)('kpi_passport_summary', {
        p_profile_id: opts.profileId,
        p_start_at: opts.startAt,
        p_end_at: opts.endAt,
      });

      if (error) throw error;
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    },
    enabled: Boolean(opts.profileId && opts.startAt && opts.endAt),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
